import delay from '@/helpers/delay';
import submitContactForm, {
  ContactFormOptions,
} from '@/helpers/submitContactForm';
import { UseFormState } from '@/hooks/useForm/interfaces';
import { ButtonSubmitStateType } from '@uikit/components/ButtonSubmit/interfaces';
import { useState } from 'react';
import { useMutation } from 'react-query';
import useDataLayer from '../useDataLayer/useDataLayer';

const handleSubmitContactForm = async <T extends unknown>(
  ...args: Parameters<typeof submitContactForm>
) => {
  const response = await submitContactForm(...args);

  if (response.status !== 200) {
    throw new Error(response.statusText);
  }

  const json = await response.json();

  if (json?.message !== 'success') {
    throw new Error(json?.message);
  }

  return json as T;
};

const useSubmitContactForm = ({
  onSuccess,
  getCaptchaSolution,
  ...options
}: Omit<ContactFormOptions, 'frcCaptchaSolution'> & {
  onSuccess?: () => void;
  getCaptchaSolution: () => Promise<string>;
}) => {
  const [submitButtonStatus, setButtonState] =
    useState<ButtonSubmitStateType>('initial');
  const { pushDataLayerEvent } = useDataLayer();

  const { mutate: submitContactForm, ...rest } = useMutation(
    async ({ keysOrder, ...data }: UseFormState & { keysOrder?: string[] }) => {
      const frcCaptchaSolution = await getCaptchaSolution();

      return await handleSubmitContactForm(data, {
        ...options,
        keysOrder: keysOrder ?? options.keysOrder,
        frcCaptchaSolution,
      });
    },
    {
      onMutate: async () => {
        setButtonState('pending');
      },
      onSuccess: async () => {
        setButtonState('success');
        onSuccess?.();
        pushDataLayerEvent({
          event: 'Formular abgeschickt',
          formName: options.formName,
        });
      },
      onError: async () => {
        setButtonState('fail');
        await delay(2000);
        setButtonState('initial');
      },
    }
  );

  return { submitContactForm, submitButtonStatus, ...rest };
};

export default useSubmitContactForm;
